import {
  ChangeEventHandler,
  forwardRef,
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';

import Tooltip from '@mui/material/Tooltip';

import './text-field.scss';

export interface TextFieldRef {
  isBlank: () => boolean;
  isValid: () => boolean;
  revalidate: () => void;
  /* drop an advisory warning the owner knows no longer applies; a blocking one is left alone */
  clearAdvisory: () => void;
  setCustomValidity: HTMLInputElement['setCustomValidity'];
}

export enum ValidationStatus {
  DEFAULT,
  LOADING,
  SUCCESS,
  WARNING
}

export interface ValidationResult {
  status?: Exclude<ValidationStatus, ValidationStatus.LOADING>;
  error?: string;
  value?: string;
  /*
   * The warning is shown but does not make the input invalid, so the form can still be submitted.
   * For what the operator should know but may save past — e.g. a fleet file that disagrees with them.
   */
  advisory?: boolean;
}

type TextFieldProps = {
  className?: string;
  debounceTime?: number;
  disabled?: boolean;
  id?: string;
  inputClassName?: string;
  inputMode?: 'search' | 'text' | 'email' | 'tel' | 'url' | 'none' | 'numeric' | 'decimal';
  label?: string;
  name?: string;
  required?: boolean;
  value: string;
  onChange: (input: string) => void;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  validate?: (input: string) => Promise<ValidationResult>;
  /*
   * Called when the operator presses the revalidate button, before validation re-runs — so a field
   * whose validation reads a cache can drop the cached answer. Not called by `ref.revalidate()`,
   * which other fields use to re-check against unchanged data.
   */
  onRevalidate?: () => void;
  allowLowercase?: boolean;
  allowSpaces?: boolean;
  ['data-cy']?: string;
};

export const TextField = forwardRef<TextFieldRef, TextFieldProps>(
  ({ debounceTime, validate, onRevalidate, value, allowLowercase, allowSpaces, onChange, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout>();

    const [lastValue, setLastValue] = useState<string>();
    const [validity, setValidity] = useState<ValidationStatus>(ValidationStatus.DEFAULT);
    /*
     * The warning text is also held in state, not only in the input's customValidity.
     *
     * The native bubble is shown at most once, and only when focus has not already moved on (see
     * handleValidation). An operator who types a unit number and tabs straight to the next field
     * therefore gets the warning triangle with no text. The message was always recoverable - the
     * triangle reports validity on click - but nothing said so. Keeping a copy here lets the
     * triangle carry the message on hover and expose it as its accessible name.
     */
    const [validationMessage, setValidationMessage] = useState('');
    const [advisory, setAdvisory] = useState(false);

    const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
      e => {
        if (validate) {
          e.target.setCustomValidity('Validating...'); /* consider invalid until validation handled */
        }
        let value = e.target.value;
        if (!allowLowercase) {
          value = value.toUpperCase();
        }
        if (!allowSpaces) {
          value = value.trim();
        }
        onChange(value);
      },
      [onChange, validate, allowLowercase, allowSpaces]
    );

    const handleValidation = useCallback(async () => {
      if (!validate || value === lastValue) {
        return; /* avoid revalidating most recent value */
      }

      setValidity(ValidationStatus.LOADING);
      const result = await validate(value);
      setLastValue(result.value ?? value);

      setAdvisory(!!result.error && !!result.advisory);
      if (!result.error) {
        setValidity(result.status ?? ValidationStatus.SUCCESS);
        setValidationMessage('');
        inputRef.current?.setCustomValidity('');
      } else if (result.advisory) {
        setValidity(result.status ?? ValidationStatus.WARNING);
        setValidationMessage(result.error);
        inputRef.current?.setCustomValidity('');
      } else {
        setValidity(result.status ?? ValidationStatus.WARNING);
        setValidationMessage(result.error);
        inputRef.current?.setCustomValidity(result.error);
        if (document.activeElement?.isSameNode(inputRef.current) || document.activeElement?.isSameNode(document.body)) {
          inputRef.current?.reportValidity(); /* display error message only if no other input focused */
        }
      }
    }, [lastValue, value, validate, setValidity]);

    const debounceValidation = useMemo(() => {
      return () => {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(handleValidation, debounceTime ?? 1000);
      };
    }, [debounceTime, handleValidation]);

    useEffect(debounceValidation, [debounceValidation]);

    /* allow parent components to access input value and validation handler via ref */
    useImperativeHandle(
      ref,
      () => ({
        isBlank: () => !value.length,
        isValid: () => inputRef.current?.validity.valid ?? validity !== ValidationStatus.WARNING,
        revalidate: () => setLastValue(undefined),
        clearAdvisory: () => {
          if (!advisory) return;
          setAdvisory(false);
          setValidity(ValidationStatus.SUCCESS);
          setValidationMessage('');
        },
        setCustomValidity: (message: string) => {
          /* keep the tooltip in step with messages pushed by the parent, e.g. a save-time conflict */
          setValidationMessage(message);
          inputRef.current?.setCustomValidity(message);
          inputRef.current?.reportValidity();
        }
      }),
      [advisory, validity, value.length, setLastValue]
    );

    return (
      <div className="d-flex flex-fill">
        <div className={`text-field flex-fill ${props.className ?? ''}`}>
          <input
            type="text"
            inputMode={props.inputMode}
            className={`w-100 ${!value.length ? 'blank' : ''} ${props.inputClassName}`}
            disabled={props.disabled}
            id={props.id}
            name={props.name}
            onChange={handleChange}
            ref={inputRef}
            required={props.required}
            value={value ?? ''}
            autoComplete="off"
            onKeyDown={props.onKeyDown}
            data-cy={props['data-cy']}
          />
          {!!props.label && (
            <label htmlFor={props.id}>
              {props.label}
              {props.required && ' *'}
            </label>
          )}
          {!props.disabled && Boolean(value?.length) && (
            <svg
              className={`clear-btn ${validate || validity !== ValidationStatus.DEFAULT ? 'with-adornment' : ''}`}
              focusable="false"
              aria-hidden="true"
              viewBox="0 0 24 24"
              onClick={() => {
                onChange('');
                setTimeout(() => {
                  inputRef.current?.focus();
                }, 0);
              }}
            >
              <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
            </svg>
          )}
          <span className="adornment loading" hidden={validity !== ValidationStatus.LOADING}>
            <svg viewBox="0 0 24 24">
              <circle r={9} cx={12} cy={12}></circle>
            </svg>
          </span>
          <span
            className="adornment material-icons icon-large"
            data-cy="validation-success"
            hidden={validity !== ValidationStatus.SUCCESS}
          >
            done
          </span>
          <Tooltip title={validationMessage} enterTouchDelay={0} describeChild>
            <span
              className="adornment material-icons icon-large warning"
              data-cy="validation-warning"
              aria-label={validationMessage}
              hidden={validity !== ValidationStatus.WARNING}
              onClick={validity !== ValidationStatus.WARNING ? undefined : () => inputRef.current?.reportValidity()}
            >
              warning
            </span>
          </Tooltip>
          {!!validate && (
            <span className="adornment material-icons icon-large" hidden={validity !== ValidationStatus.DEFAULT}>
              search
            </span>
          )}
        </div>
        {!!validate && (validity === ValidationStatus.SUCCESS || validity === ValidationStatus.WARNING) && (
          <button
            className="btn-back"
            data-cy="revalidate"
            type="button"
            onClick={() => {
              onRevalidate?.();
              setValidity(ValidationStatus.LOADING);
              setLastValue(undefined);
            }}
          >
            <span className="material-icons icon-large p-1">replay</span>
          </button>
        )}
      </div>
    );
  }
);
